import { config as envConfig } from 'ENV_CONFIG';
import { OCAB_QUESTION_TYPE } from './builder';
import { sortMultipleAnswers } from './format-answers';
import { formatAnswersHelpers } from './format-answers.helpers';
import { formatAddressAnswerToString, formatFormattedAddressAnswerToString } from './address';
import { api } from '~/api';

const COMPOSED_ANSWER_DELIMITER = ':';
export const getHeightAnswer = ({ feet, inches, cm }) => {
  const values = [];
  if (feet) values.push(`${feet} feet`);
  if (inches) values.push(`${inches} inches`);
  if (cm) values.push(`${cm} cm`);

  return [values.join(', ')];
};

export const getDropdownWithPriceAnswer = (question, answers) => {
  const rawAnswer = answers?.[0] || '';

  if (!rawAnswer) return [''];

  const legacyOptionName = rawAnswer.split('-')[0].trim();
  const answeredOption = question.options.find(
    (option) =>
      option.displayName === rawAnswer ||
      option.name === rawAnswer ||
      option.name === legacyOptionName,
  );

  return [answeredOption?.name || rawAnswer];
};

export const isComposedAnswer = (answer) => answer.includes(COMPOSED_ANSWER_DELIMITER);
export const getComposedAnswer = (answer) =>
  answer
    .split(COMPOSED_ANSWER_DELIMITER)
    .map((it) => it.trim())
    .filter(Boolean);
export const composeAnswer = ({ displayName, description }) =>
  description ? `${displayName} ${COMPOSED_ANSWER_DELIMITER} ${description}` : displayName;

export const getFormattedAnswer = (question, answers) => {
  if (!answers) {
    return {
      questionId: question.id,
      answers: [''],
    };
  }
  switch (question.type) {
    case OCAB_QUESTION_TYPE.HEIGHT:
      return {
        questionId: question.id,
        answers: getHeightAnswer(answers),
      };
    case OCAB_QUESTION_TYPE.RADIO_PRICE:
    case OCAB_QUESTION_TYPE.DROPDOWN_PRICE:
      return {
        questionId: question.id,
        answers: getDropdownWithPriceAnswer(question, answers),
      };
    case OCAB_QUESTION_TYPE.CHECKBOX:
      return {
        questionId: question.id,
        answers: [answers[0] ? '1' : '0'],
      };
    case OCAB_QUESTION_TYPE.DROPDOWN_CHECKBOX: {
      return {
        questionId: question.id,
        answers: [formatAnswersHelpers.getAnswer(answers) ? question.label : ''],
      };
    }
    case OCAB_QUESTION_TYPE.CHECKBOXES:
      return {
        questionId: question.id,
        answers,
      };
    default:
      return {
        questionId: question.id,
        answers: sortMultipleAnswers(question.options, answers),
      };
  }
};

const checkQuestionIsPresent = (sectionAnswers, question) =>
  Object.keys(sectionAnswers).find((key) => key.endsWith(question.id));

const checkChildQuestionIsDisplayed = (childQuestion, parentQuestion, sectionAnswers) => {
  const displayConditionValues = Object.values(childQuestion.displayCondition.selectedOptions);

  const parentAnswer =
    parentQuestion.type === OCAB_QUESTION_TYPE.CHECKBOXES
      ? sectionAnswers[parentQuestion.reduxFieldConfig.name]
      : formatAnswersHelpers.getAnswer(sectionAnswers[parentQuestion.reduxFieldConfig.name]);

  if (parentQuestion.type === OCAB_QUESTION_TYPE.CHECKBOX)
    return parentAnswer === Boolean(displayConditionValues?.length);

  if (parentQuestion.type === OCAB_QUESTION_TYPE.CHECKBOXES && parentAnswer)
    return parentAnswer.some((answer) => displayConditionValues.includes(answer));

  return displayConditionValues.includes(parentAnswer);
};

export function getSectionAnswers(questions, sectionAnswers) {
  return Object.keys(questions).reduce((acc, curr) => {
    const resultFrom = questions[curr].map((question) => {
      const childrenAnswers = question.children
        ? question.children
            .filter((child) => checkChildQuestionIsDisplayed(child, question, sectionAnswers))
            .map((child) => {
              const childAnswers = sectionAnswers[child.reduxFieldConfig.name];

              return (
                checkQuestionIsPresent(sectionAnswers, child) &&
                getFormattedAnswer(child, childAnswers)
              );
            })
        : [];

      const answers = sectionAnswers[question.reduxFieldConfig.name];

      return (
        checkQuestionIsPresent(sectionAnswers, question) && [
          getFormattedAnswer(question, answers),
          ...childrenAnswers,
        ]
      );
    });

    return acc.concat(...resultFrom).filter((item) => Boolean(item));
  }, []);
}

export const getAnswersFromForm = (questions, formAnswers) =>
  Object.keys(formAnswers).reduce(
    (acc, curr) => [...acc, ...getSectionAnswers(questions, formAnswers[curr])],
    [],
  );

export const getAddressAnswer = (address, appointmentTypeId) => {
  const { addressQuestionId } =
    envConfig.services[api.SERVICE_NAME].appointmentTypes[appointmentTypeId];
  return addressQuestionId && address
    ? [
        {
          questionId: addressQuestionId,
          answers: address.id ? [address.description] : formatAddressAnswerToString(address),
        },
      ]
    : [];
};

export const getFormAnswers = (questions, formAnswers, address, appointmentTypeId) => {
  const { formattedAddressQuestionId } =
    envConfig.services[api.SERVICE_NAME].appointmentTypes[appointmentTypeId];
  const formattedAddressAnswers =
    formattedAddressQuestionId && address
      ? [
          {
            questionId: formattedAddressQuestionId,
            answers: address.id
              ? [address.description]
              : formatFormattedAddressAnswerToString(address),
          },
        ]
      : [];

  return [
    ...getAnswersFromForm(questions, formAnswers),
    ...getAddressAnswer(address, appointmentTypeId),
    ...formattedAddressAnswers,
  ];
};

export const getParentQuestionsReduxNames = (questions) =>
  questions.map((q) => q.reduxFieldConfig.name);
export const getAllQuestionsReduxNames = (questions) =>
  questions.reduce(
    (curr, q) => curr.concat(getParentQuestionsReduxNames(q.children)),
    getParentQuestionsReduxNames(questions),
  );

const formatSelectedOptionToOcabFormat = (question, answer) => {
  switch (question.type) {
    case OCAB_QUESTION_TYPE.CHECKBOX: {
      return answer === '1';
    }
    default:
      return answer;
  }
};

export const flatQuestionChildren = (questions, answers) =>
  questions.reduce((questionsAcc, question) => {
    const answeredQuestionsChildren = question.children
      .map((child) => {
        const parentQuestionAnswer =
          question.type === OCAB_QUESTION_TYPE.CHECKBOXES
            ? answers[question.reduxFieldConfig.name]
            : formatAnswersHelpers.getAnswer(answers[question.reduxFieldConfig.name]);

        const displayConditionValues = Object.values(child.displayCondition.selectedOptions);

        const shouldChildQuestionBeDisplayed =
          question.type === OCAB_QUESTION_TYPE.CHECKBOXES && parentQuestionAnswer
            ? parentQuestionAnswer.some((value) => displayConditionValues.includes(value))
            : displayConditionValues.some(
                (value) =>
                  formatSelectedOptionToOcabFormat(question, value) === parentQuestionAnswer,
              );

        return shouldChildQuestionBeDisplayed && child;
      })
      .filter(Boolean);

    return [...questionsAcc, question, ...answeredQuestionsChildren];
  }, []);
